import {
  type GmMenuItemFilters,
  type GmMenuItemSortingOptions,
  type VendorCategoryItemsPage,
  type VendorMenuCategoryConnection,
  type VendorMenuFiltersInput,
  type VendorMenuItemStat,
} from '@/generated/graphql'
import { gql } from '@apollo/client'

const MENU_ITEM_FRAGMENTS = gql`
  fragment MenuItemVariation on MenuItemVariation {
    categoryId
    categoryIdArray
    servings
    popularity
    packaging
    minQty
    price
    priceExTax
    taxValue
    priceCustomerFacing
    priceCustomerFacingExTax
    privateAccessAccountIdArray
    taxValueCustomerFacing
    isCombo
    isPricePerHead
    position
    isPrivate
  }

  fragment MenuOptionMenuItem on MenuItem {
    _id
    isActive
    activeFrom
    activeTo
    updatedAt
    name
    shortDescription
    description
    dietaries
    allergens
    translations {
      key
      value
      language
    }
    subAllergens {
      cereals
      nuts
    }
  }

  fragment MenuOptionItem on OptionItem {
    _id
    price
    priceExTax
    priceCustomerFacing
    priceCustomerFacingExTax
    taxRate
    taxValue
    taxValueCustomerFacing
    item {
      ...MenuOptionMenuItem
    }
  }

  fragment MenuItemOption on MenuItemOptions {
    _id
    optionType
    name
    position
    minQty
    maxQty
    qtyPerPerson
    categoryType
    items {
      ...MenuOptionItem
    }
  }

  fragment MenuItem on MenuItem {
    _id
    isActive
    name
    image
    shortDescription
    description
    ingredients
    price
    dietaries
    allergens
    taxRate
    subAllergens {
      nuts
      cereals
    }
    variations(varType: "gm") {
      _id
      ...MenuItemVariation
    }
    updatedAt
    isHot
    activeFrom
    activeTo
    macros_carbs
    macros_fat
    macros_protein
    macros_kcal
    macros_salt
    macros_saturates
    macros_sugar
    macros_fibre
    options {
      ...MenuItemOption
    }
    translations {
      key
      value
      language
    }
  }
`

export type VendorMenuCategoriesQueryData = {
  vendorMenuCategories: VendorMenuCategoryConnection
}

export type VendorMenuCategoriesQueryVariables = {
  vendorId: string
  filters?: VendorMenuFiltersInput | null
  offset?: number
  limit?: number
  previewLimit?: number
}

export const GET_VENDOR_MENU_CATEGORIES = gql`
  query GetVendorMenuCategories(
    $vendorId: ID!
    $filters: VendorMenuFiltersInput
    $offset: Int = 0
    $limit: Int = 50
    $previewLimit: Int = 20
  ) {
    vendorMenuCategories(
      vendorId: $vendorId
      filters: $filters
      offset: $offset
      limit: $limit
      previewLimit: $previewLimit
    ) {
      pageInfo {
        count
        limit
        offset
      }
      rows {
        id
        name
        description
        position
        itemCount
        isEmpty
        isFeatured
        hasMoreItems
        heroAssets {
          url
          altText
          type
        }
      }
    }
  }
`

export type VendorCategoryItemsQueryData = {
  vendorCategoryItems: VendorCategoryItemsPage
}

export type VendorCategoryItemsQueryVariables = {
  vendorId: string
  categoryId: string
  filters?: VendorMenuFiltersInput | null
  offset?: number
  limit?: number
}

export const GET_VENDOR_CATEGORY_ITEMS = gql`
  ${MENU_ITEM_FRAGMENTS}
  query GetVendorCategoryItems(
    $vendorId: ID!
    $categoryId: ID!
    $filters: VendorMenuFiltersInput
    $offset: Int = 0
    $limit: Int = 20
  ) {
    vendorCategoryItems(
      vendorId: $vendorId
      categoryId: $categoryId
      filters: $filters
      offset: $offset
      limit: $limit
    ) {
      categoryId
      pageInfo {
        count
        hasMore
        limit
        nextOffset
        offset
      }
      items {
        ...MenuItem
      }
    }
  }
`

export type LiveMenuItemQueryData = {
  liveMenuItem: import('@/generated/graphql').MenuItemFragment | null
}

export type LiveMenuItemQueryVariables = {
  id: string
}

export const GET_LIVE_MENU_ITEM = gql`
  ${MENU_ITEM_FRAGMENTS}
  query GetLiveMenuItemForReorder($id: ID!) {
    liveMenuItem(id: $id) {
      ...MenuItem
    }
  }
`

export type VendorMenuFilterStats = {
  allergens: VendorMenuItemStat[]
  dietaries: VendorMenuItemStat[]
  categories: VendorMenuItemStat[]
}

export type VendorMenuFilterStatsQueryData = {
  availability: {
    id: string
    itemsPagedv2: {
      pageInfo: {
        count: number
      }
      stats: VendorMenuFilterStats
    }
  } | null
}

export type VendorMenuFilterStatsQueryVariables = {
  vendorId: string
  filters?: GmMenuItemFilters | null
  sorting?: GmMenuItemSortingOptions | null
}

export const GET_VENDOR_MENU_FILTER_STATS = gql`
  query GetVendorMenuFilterStats(
    $vendorId: ID!
    $filters: GmMenuItemFilters
    $sorting: GmMenuItemSortingOptions
  ) {
    availability: gmAvailabilityByVendorId(vendorId: $vendorId) {
      id
      itemsPagedv2(limit: 1, offset: 0, filters: $filters, sorting: $sorting) {
        pageInfo {
          count
        }
        stats {
          allergens {
            id
            label
            value
            name
          }
          dietaries {
            id
            label
            value
            name
          }
          categories {
            id
            label
            value
            name
          }
        }
      }
    }
  }
`
